import { MIN_VOTES_FOR_RATIO, type FeedbackTally } from "@skills-marketplace/contracts/contract-8-feedback";

/**
 * What a surface should render for an entry's usefulness.
 *
 * A discriminated union rather than a formatted string so each surface can make
 * its own layout call (see `unvoted` below) while the *copy* stays centralised.
 */
export type UsefulnessDisplay =
  /**
   * No votes at all.
   *
   * Reads IDENTICALLY to `insufficient` — both show "Not enough votes yet" — because
   * #620's criterion is "fewer than 5 total votes" with no carve-out for zero, and
   * zero is fewer than five.
   *
   * The prototype does distinguish them (its card helper returns "" at zero while its
   * detail block shows the message), and an earlier revision here followed that on the
   * reasoning that ~1500 cards repeating one disclaimer is noise. Overruled by the
   * written criterion: a card that shows nothing is indistinguishable from a card
   * whose signal failed to load, whereas the message says "asked and answered: not
   * yet".
   *
   * Kept as a SEPARATE state from `insufficient` even so, because the detail page must
   * suppress its "N votes so far" sub-line here — "0 votes so far" is noise where
   * "3 votes so far" is information.
   */
  | { kind: "unvoted" }
  /**
   * Between 1 and MIN_VOTES_FOR_RATIO - 1 votes: too few to quote a ratio.
   *
   * #620: a single up-vote would otherwise read "100% useful", which is more
   * confident than one vote earns. Suppressing the number also stops a lightly
   * voted entry from reading as bad when it is merely new.
   */
  | { kind: "insufficient"; total: number }
  /** At or above the gate — the ratio is worth quoting. */
  | { kind: "rated"; useful: number; total: number };

/**
 * Derive the display state for an entry's tally.
 *
 * `undefined` means the entry has no tally in the artifact/response at all, which
 * is the same user-facing state as a tally of zero votes — absence and zero are
 * ONE state here, so callers never need to distinguish them.
 */
export function usefulnessDisplay(tally: FeedbackTally | undefined): UsefulnessDisplay {
  if (!tally || tally.total <= 0) return { kind: "unvoted" };
  if (tally.total < MIN_VOTES_FOR_RATIO) {
    return { kind: "insufficient", total: tally.total };
  }
  return { kind: "rated", useful: tally.useful, total: tally.total };
}
